import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { exportCsv } from '../../utils/csv.js';
import { formatCurrency } from '../../utils/formatters.js';

/**
 * Die drei Abschlüsse: Rohbilanz, Erfolgsrechnung, Bilanz.
 *
 * Vorher holte diese Seite alle drei OHNE Zeitraum. Eine Erfolgsrechnung über
 * alle Zeiten ist keine Erfolgsrechnung - sie beantwortet keine Frage, die
 * jemand stellt. Und sie zeigte nur Summen; welches Konto welchen Beitrag
 * leistet, stand nirgends. Genau das liest ein Treuhänder.
 */

/** Ein Datum als JJJJ-MM-TT, aus den LOKALEN Bestandteilen. */
function alsTag(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Die gebräuchlichen Zeiträume.
 *
 * Sie werden beim Klick gerechnet, nicht beim Laden des Moduls: eine Seite,
 * die über Mitternacht offen bleibt, böte sonst weiter den Zeitraum von
 * gestern an.
 */
const ZEITRAEUME = [
  {
    key: 'thisYear',
    berechne: () => {
      const j = new Date().getFullYear();
      return { from: `${j}-01-01`, to: `${j}-12-31` };
    },
  },
  {
    key: 'lastYear',
    berechne: () => {
      const j = new Date().getFullYear() - 1;
      return { from: `${j}-01-01`, to: `${j}-12-31` };
    },
  },
  {
    key: 'thisQuarter',
    berechne: () => {
      const heute = new Date();
      const q = Math.floor(heute.getMonth() / 3);
      return {
        from: alsTag(new Date(heute.getFullYear(), q * 3, 1)),
        to: alsTag(new Date(heute.getFullYear(), q * 3 + 3, 0)),
      };
    },
  },
  {
    key: 'thisMonth',
    berechne: () => {
      const heute = new Date();
      return {
        from: alsTag(new Date(heute.getFullYear(), heute.getMonth(), 1)),
        to: alsTag(new Date(heute.getFullYear(), heute.getMonth() + 1, 0)),
      };
    },
  },
];

export function ReportsPage() {
  const { t } = useTranslation();
  const [zeitraum, setZeitraum] = useState(() => ZEITRAEUME[0].berechne());

  const periode = useMemo(
    () => ({
      ...(zeitraum.from ? { from: zeitraum.from } : {}),
      ...(zeitraum.to ? { to: zeitraum.to } : {}),
    }),
    [zeitraum],
  );

  const trial = useQuery({
    queryKey: ['finance', 'reports', 'trial-balance', periode],
    queryFn: () => financeApi.trialBalance(periode).then((r) => r.items),
    placeholderData: keepPreviousData,
  });
  const income = useQuery({
    queryKey: ['finance', 'reports', 'income', periode],
    queryFn: () => financeApi.income(periode).then((r) => r.items),
    placeholderData: keepPreviousData,
  });

  /**
   * Die Bilanz ist ein STICHTAG, kein Zeitraum.
   *
   * Sie nimmt `asOf`, nicht `from`/`to`. Würde die Seite hier den Zeitraum
   * mitschicken, verwürfe Joi ihn stillschweigend (stripUnknown) - und der
   * Benutzer bekäme die Bilanz von heute unter der Überschrift seines
   * gewählten Zeitraums. Nichts stürzt ab, die Zahlen sind nur die falschen.
   *
   * Das Ende des Zeitraums IST der Stichtag: die Bilanz zum 31.12. gehört zur
   * Erfolgsrechnung des Jahres.
   */
  const stichtag = zeitraum.to || alsTag(new Date());
  const balanceSheet = useQuery({
    queryKey: ['finance', 'reports', 'balance-sheet', stichtag],
    queryFn: () => financeApi.balanceSheet({ asOf: stichtag }).then((r) => r.items),
    placeholderData: keepPreviousData,
  });

  const setzeZeitraum = (feld) => (e) => setZeitraum((z) => ({ ...z, [feld]: e.target.value }));

  const kontoSpalten = (betragsFeld, betragsLabel) => [
    { key: 'accountCode', label: t('col.code'), render: (r) => <span className="mono">{r.accountCode}</span> },
    { key: 'accountName', label: t('col.name') },
    { key: betragsFeld, label: betragsLabel, align: 'right', render: (r) => formatCurrency(r[betragsFeld]) },
  ];

  const ausfuhr = (name, rows, betragsFeld, betragsLabel) => () => exportCsv(
    `${name}-${zeitraum.from || 'anfang'}-bis-${zeitraum.to || 'heute'}`,
    rows,
    [
      { key: 'accountCode', label: t('col.code') },
      { key: 'accountName', label: t('col.name') },
      { key: betragsFeld, label: betragsLabel },
    ],
  );

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('finance.reports.title')}</h1>
          <p className="muted">{t('finance.reports.subtitle')}</p>
        </div>
      </header>

      <div className="toolbar">
        <label className="field">
          <span className="field-label">{t('journal.filter.from')}</span>
          <input className="input" type="date" value={zeitraum.from} onChange={setzeZeitraum('from')} />
        </label>
        <label className="field">
          <span className="field-label">{t('journal.filter.to')}</span>
          <input className="input" type="date" value={zeitraum.to} onChange={setzeZeitraum('to')} />
        </label>
        {ZEITRAEUME.map((z) => (
          <Button key={z.key} size="sm" onClick={() => setZeitraum(z.berechne())}>
            {t(`finance.reports.range.${z.key}`)}
          </Button>
        ))}
      </div>

      <Card
        title={t('finance.reports.trialBalance')}
        actions={trial.data?.totals && (
          <span className="muted text-sm">
            {t('finance.reports.totals.debits')} {formatCurrency(trial.data.totals.debit)} ·{' '}
            {t('finance.reports.totals.credits')} {formatCurrency(trial.data.totals.credit)} ·{' '}
            {trial.data.totals.isBalanced
              ? t('finance.reports.totals.balanced')
              : t('finance.reports.totals.unbalanced')}
          </span>
        )}
      >
        <Table
          isLoading={trial.isLoading}
          rows={trial.data?.lines || []}
          getRowKey={(row) => row.accountId}
          emptyMessage={t('finance.reports.empty')}
          columns={[
            { key: 'accountCode', label: t('col.code'), render: (r) => <span className="mono">{r.accountCode}</span> },
            { key: 'accountName', label: t('col.name') },
            { key: 'accountType', label: t('col.type'), render: (r) => t(`accountType.${r.accountType}`) },
            { key: 'debit', label: t('finance.reports.col.debit'), align: 'right', render: (r) => formatCurrency(r.debit) },
            { key: 'credit', label: t('finance.reports.col.credit'), align: 'right', render: (r) => formatCurrency(r.credit) },
          ]}
        />
      </Card>

      <Card
        title={t('finance.reports.income')}
        actions={(
          <Button
            size="sm"
            disabled={!income.data}
            onClick={ausfuhr(
              t('finance.reports.file.income'),
              [...(income.data?.revenue || []), ...(income.data?.expenses || [])],
              'amount',
              t('finance.reports.col.amount'),
            )}
          >
            {t('finance.reports.action.export')}
          </Button>
        )}
      >
        <div className="card-grid">
          <div className="metric-card">
            <span className="metric-label">{t('finance.reports.metric.revenue')}</span>
            <span className="metric-value">{formatCurrency(income.data?.totals?.revenue)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{t('finance.reports.metric.expenses')}</span>
            <span className="metric-value">{formatCurrency(income.data?.totals?.expenses)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{t('finance.reports.metric.netIncome')}</span>
            <span className="metric-value">{formatCurrency(income.data?.totals?.netIncome)}</span>
          </div>
        </div>

        <h3 className="report-section">{t('finance.reports.section.revenue')}</h3>
        <Table
          isLoading={income.isLoading}
          rows={income.data?.revenue || []}
          getRowKey={(row) => row.accountId}
          emptyMessage={t('finance.reports.empty')}
          columns={kontoSpalten('amount', t('finance.reports.col.amount'))}
        />

        <h3 className="report-section">{t('finance.reports.section.expenses')}</h3>
        <Table
          isLoading={income.isLoading}
          rows={income.data?.expenses || []}
          getRowKey={(row) => row.accountId}
          emptyMessage={t('finance.reports.empty')}
          columns={kontoSpalten('amount', t('finance.reports.col.amount'))}
        />
      </Card>

      <Card
        title={t('finance.reports.balanceSheet')}
        actions={(
          <span className="report-card-actions">
            {/* Der Stichtag steht dabei - sonst liest man die Bilanz als
                Zeitraum, und das ist sie nicht. */}
            <span className="muted text-sm">{t('finance.reports.asOf', { date: stichtag })}</span>
            {balanceSheet.data?.totals && (
              <span className="muted text-sm">
                {balanceSheet.data.totals.isBalanced
                  ? t('finance.reports.totals.assetsEquation')
                  : t('finance.reports.totals.unbalanced')}
              </span>
            )}
            <Button
              size="sm"
              disabled={!balanceSheet.data}
              onClick={ausfuhr(
                t('finance.reports.file.balanceSheet'),
                [
                  ...(balanceSheet.data?.assets || []),
                  ...(balanceSheet.data?.liabilities || []),
                  ...(balanceSheet.data?.equity || []),
                ],
                'balance',
                t('finance.reports.col.balance'),
              )}
            >
              {t('finance.reports.action.export')}
            </Button>
          </span>
        )}
      >
        <div className="card-grid">
          <div className="metric-card">
            <span className="metric-label">{t('finance.reports.metric.assets')}</span>
            <span className="metric-value">{formatCurrency(balanceSheet.data?.totals?.assets)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{t('finance.reports.metric.liabilities')}</span>
            <span className="metric-value">{formatCurrency(balanceSheet.data?.totals?.liabilities)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{t('finance.reports.metric.equity')}</span>
            <span className="metric-value">{formatCurrency(balanceSheet.data?.totals?.equity)}</span>
          </div>
        </div>

        <h3 className="report-section">{t('finance.reports.section.assets')}</h3>
        <Table
          isLoading={balanceSheet.isLoading}
          rows={balanceSheet.data?.assets || []}
          getRowKey={(row) => row.accountId}
          emptyMessage={t('finance.reports.empty')}
          columns={kontoSpalten('balance', t('finance.reports.col.balance'))}
        />

        <h3 className="report-section">{t('finance.reports.section.liabilities')}</h3>
        <Table
          isLoading={balanceSheet.isLoading}
          rows={balanceSheet.data?.liabilities || []}
          getRowKey={(row) => row.accountId}
          emptyMessage={t('finance.reports.empty')}
          columns={kontoSpalten('balance', t('finance.reports.col.balance'))}
        />

        <h3 className="report-section">{t('finance.reports.section.equity')}</h3>
        <Table
          isLoading={balanceSheet.isLoading}
          rows={balanceSheet.data?.equity || []}
          getRowKey={(row) => row.accountId}
          emptyMessage={t('finance.reports.empty')}
          columns={kontoSpalten('balance', t('finance.reports.col.balance'))}
        />
        {/* Der laufende Gewinn gehört ins Eigenkapital, steht aber auf keinem
            Konto - er ist die Differenz aus Ertrag und Aufwand bis zum
            Stichtag. Ohne diese Zeile geht die Bilanz auf dem Papier nicht
            auf, obwohl sie es tut. */}
        {balanceSheet.data?.retainedEarnings != null && (
          <p className="muted report-note">
            {t('finance.reports.retainedEarnings', {
              amount: formatCurrency(balanceSheet.data.retainedEarnings),
            })}
          </p>
        )}
      </Card>
    </section>
  );
}
